import { Outlet } from 'react-router-dom'
import { SidebarNav } from '@/components/layout/SidebarNav'
import { TopBar } from '@/components/layout/TopBar'
import { Breadcrumbs } from '@/components/layout/Breadcrumbs'
import { BreadcrumbProvider } from '@/components/layout/BreadcrumbContext'
import { SidebarProvider, useSidebar } from '@/contexts/SidebarContext'
import { useOrganization } from '@/contexts/OrganizationContext'
import { cn } from '@atrvd/saas-ui'

/**
 * Inner layout content that consumes sidebar context for dynamic margin
 */
function DashboardContent() {
  const { isCollapsed } = useSidebar()
  const { currentOrganization } = useOrganization()

  return (
    <div className="h-screen overflow-hidden bg-background flex flex-col">
      {/* Top Bar - fixed height, never scrolls */}
      <TopBar />

      {/* Sidebar - fixed, starts below TopBar (top-16 = 64px) */}
      <SidebarNav />

      {/* Main content area - offset by sidebar width, fills remaining height */}
      <div
        className={cn(
          'flex-1 flex flex-col overflow-hidden transition-[margin-left] duration-300',
          isCollapsed ? 'ml-16' : 'ml-64'
        )}
      >
        {/* Breadcrumbs header - fixed, never scrolls */}
        <div className="flex-shrink-0 bg-background px-6 py-3">
          <Breadcrumbs />
        </div>

        {/* Page content - scrollable area. The organization switcher does not
            navigate, so the key remounts the page: its local state (table page,
            selection, open dialog, draft) belongs to the previous organization. */}
        <main className="flex-1 overflow-y-auto p-6">
          <Outlet key={currentOrganization?.id} />
        </main>
      </div>
    </div>
  )
}

/**
 * Main dashboard layout with top bar, sidebar navigation, and content area.
 * Wraps content in SidebarProvider for collapse state management.
 *
 * Layout structure:
 * ┌─────────────────────────────────────────────────────────┐
 * │ TopBar (full width, h-16, fixed)                        │
 * ├──────────┬──────────────────────────────────────────────┤
 * │          │ Breadcrumbs (fixed)                          │
 * │ Sidebar  ├──────────────────────────────────────────────┤
 * │ (fixed,  │ Page Content (scrollable)                    │
 * │collapsible)                                             │
 * └──────────┴──────────────────────────────────────────────┘
 */
export function DashboardLayout() {
  return (
    <SidebarProvider>
      {/* BreadcrumbProvider wraps BOTH the breadcrumb bar and the routed pages
          (rendered via <Outlet/> inside DashboardContent), so a page can
          register a dynamic-segment label the bar then renders (DCP-296). */}
      <BreadcrumbProvider>
        <DashboardContent />
      </BreadcrumbProvider>
    </SidebarProvider>
  )
}